import { api } from "@/integrations/clients/carAuction/carAuctionClient";

export interface AdminForMention {
  id: string; // Actor ID
  adminId: string;
  name: string;
  email: string;
}

export interface GetAdminsForMentionsResponse {
  success: boolean;
  data: AdminForMention[];
}

export interface OnlineUser {
  id: string;
  name: string;
  email: string;
  role?: string;
  lastLogin?: string | null;
}

export interface OnlineUsersResponse {
  success: boolean;
  users: OnlineUser[];
  total: number;
  count: number;
  hasMore: boolean;
}

export interface OnlineUsersCountResponse {
  success: boolean;
  count: number;
}

export interface UpdateAdminProfileRequest {
  username?: string;
  email?: string;
  phoneNumber?: string;
  personalNumber?: string;
  firstName?: string;
  lastName?: string;
}

export interface UpdateAdminProfileResponse {
  success: boolean;
  data: unknown;
}

export interface UpdateActivityResponse {
  success: boolean;
  message?: string;
}

export class AdminService {
  /**
   * Get all admins for mentions (excluding current admin)
   * GET /admin/admins-for-mentions
   */
  static async getAdminsForMentions(): Promise<AdminForMention[]> {
    const response = await api.get<GetAdminsForMentionsResponse>("/admin/admins-for-mentions");
    return response.data || [];
  }

  /**
   * Get count of online users (optimized for polling)
   * GET /admin/online-users/count
   */
  static async getOnlineUsersCount(): Promise<number> {
    const response = await api.get<OnlineUsersCountResponse>("/admin/online-users/count");
    return response.count;
  }

  /**
   * Get online users with pagination
   * GET /admin/online-users
   */
  static async getOnlineUsers(page: number = 1, limit: number = 25): Promise<OnlineUsersResponse> {
    const response = await api.get<OnlineUsersResponse>("/admin/online-users", {
      params: { page, limit }
    });
    return response;
  }

  /**
   * Update admin activity
   * POST /admin/activity
   */
  static async updateActivity(): Promise<UpdateActivityResponse> {
    const response = await api.post<UpdateActivityResponse>("/admin/activity");
    return response;
  }

  /**
   * Update another admin's profile
   * PUT /admin/admins/:id
   */
  static async updateAdminById(
    adminId: string,
    updates: UpdateAdminProfileRequest,
  ): Promise<UpdateAdminProfileResponse> {
    const response = await api.put<UpdateAdminProfileResponse>(
      `/admin/admins/${adminId}`,
      updates,
    );
    return response;
  }

  /**
   * Delete / deactivate an admin
   * DELETE /admin/admins/:id
   */
  static async deleteAdmin(adminId: string): Promise<void> {
    const response = await api.delete<{ success: boolean }>(
      `/admin/admins/${adminId}`,
    );
    if (!response.success) {
      throw new Error("Failed to delete admin");
    }
  }
}
